import { useSingleton } from "@/other/injection";
import { MessageBusService } from "@/other/messageBus/MessageBusService";
import { useCallback } from "react";
import {
    IApiResult,
    useSubscription
} from "@/uiLibrary/virtualScroll/useSubscription";
import { Spinner } from "@/uiLibrary/spinner/Spinner";
import { Accepted } from "@/uiLibrary/icons/Accepted";
import { dateDescendingSortCompare } from "@/uiLibrary/virtualScroll/sorters";

export function PriorityCases() {
    const priorityCasesLimit = 50;
    const messageBusService = useSingleton(MessageBusService);

    const createSearchableCasesSubscription = useCallback(
        (
            callback: (
                response: IApiResult<
                    RedDeer.Contracts.Dtos.Compliance.ComplianceCaseDto,
                    string
                >
            ) => void
        ) => {
            const request: RedDeer.Contracts.Queries.Compliance.GetSearchableCasesQuery =
                {
                    MessageType: "Queries.Compliance.GetSearchableCasesQuery",
                    GetPriorityCasesOnly: true,
                    Statuses: [
                        RedDeer.Contracts.Enums.ComplianceCaseStatus.New
                    ],
                    Limit: priorityCasesLimit
                };

            return messageBusService.asyncSubscribe<RedDeer.Contracts.Queries.Compliance.GetSearchableCasesResponse>(
                {
                    request,
                    description: `Subscribing to cases`,
                    callback
                }
            );
        },
        [messageBusService]
    );
    const { items: searchableCases, hasResult } = useSubscription({
        createSubscription: createSearchableCasesSubscription,
        getItemId: x => x.Id ?? "",
        sortCompare
    });

    return (
        <div
            className={`scroll min-h-screen from-slate-50 via-blue-50/30 to-indigo-50/50 transition-colors duration-500`}
        >
            <div className="mx-auto max-w-7xl p-3">
                {/* 4. CONTEXT & 5. LIFECYCLE: Priority Cases Feed */}
                <div className="flex items-center justify-between">
                    <div>
                        <div className={`text-2xl font-bold text-slate-900`}>
                            New Priority Cases
                        </div>
                        <div className={`mb-3 text-sm text-slate-600`}>
                            Review details and take action
                        </div>
                    </div>
                </div>

                {searchableCases.length === 0 && (
                    <div className="py-12 text-center">
                        {!hasResult && <Spinner />}
                        {hasResult && (
                            <div className="py-12 text-center">
                                <Accepted size={50} />
                                <div className="mt-4">
                                    No new priority cases found.
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 3. HIERARCHY: All cases displayed with full details */}
                <div className="space-y-4">
                    {searchableCases
                        .slice(0, priorityCasesLimit)
                        .map((item, index) => {
                            return (
                                <div
                                    onClick={() =>
                                        (window.location.hash = `/Case/${item.Id ?? ""}`)
                                    }
                                    key={item.Id}
                                    className={`relative cursor-pointer rounded-xl bg-white/90 p-6 shadow-sm transition-all duration-300 hover:translate-x-0.5`}
                                >
                                    {/* Sequential number badge for clarity */}
                                    <div
                                        className={`absolute top-6 -left-3 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700 shadow-lg`}
                                    >
                                        {index + 1}
                                    </div>

                                    <div className="flex items-start justify-between">
                                        <div className="ml-3 flex flex-1 items-start gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center items-start gap-2">
                                                    <div
                                                        className={`mb-1 text-xs font-semibold text-slate-900`}
                                                    >
                                                        {item.Title}
                                                    </div>
                                                    {item.CaseTags?.map(tag => (
                                                        <span
                                                            key={tag.Id}
                                                            className="rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700"
                                                        >
                                                            {tag.Name}
                                                        </span>
                                                    ))}
                                                </div>
                                                {item.Description && (
                                                    <p
                                                        className={`mb-1 text-xs text-slate-600`}
                                                    >
                                                        {item.Description}
                                                    </p>
                                                )}

                                                {/* Expanded details always visible */}
                                                <div
                                                    className={`rounded-lg border-l-4 border-red-500 bg-indigo-50/50 p-3`}
                                                >
                                                    <div
                                                        className={`mb-2 text-xs font-semibold tracking-wide text-indigo-700 uppercase`}
                                                    >
                                                        Investigation Details
                                                    </div>
                                                    <div
                                                        className={`grid grid-cols-2 gap-x-6 text-xs leading-relaxed text-slate-700`}
                                                    >
                                                        {item.StartOfPeriodUnderInvestigation &&
                                                            item.EndOfPeriodUnderInvestigation && (
                                                                <p>
                                                                    <strong>
                                                                        Period:
                                                                    </strong>{" "}
                                                                    {new Date(
                                                                        item.StartOfPeriodUnderInvestigation
                                                                    ).toLocaleDateString()}{" "}
                                                                    -{" "}
                                                                    {new Date(
                                                                        item.EndOfPeriodUnderInvestigation
                                                                    ).toLocaleDateString()}
                                                                </p>
                                                            )}
                                                        {item.ReportedOn && (
                                                            <p>
                                                                <strong>
                                                                    Reported:
                                                                </strong>{" "}
                                                                {new Date(
                                                                    item.ReportedOn
                                                                ).toLocaleString()}
                                                            </p>
                                                        )}
                                                        {item.Securities &&
                                                            item.Securities
                                                                .length > 0 && (
                                                                <p>
                                                                    <strong>
                                                                        Securities:
                                                                    </strong>{" "}
                                                                    {item.Securities.map(
                                                                        security =>
                                                                            security.Symbol
                                                                    ).join(
                                                                        ", "
                                                                    )}
                                                                </p>
                                                            )}
                                                        {item.Traders &&
                                                            item.Traders
                                                                .length > 0 && (
                                                                <p>
                                                                    <strong>
                                                                        Traders:
                                                                    </strong>{" "}
                                                                    {item.Traders.join(
                                                                        ", "
                                                                    )}
                                                                </p>
                                                            )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
}

const sortCompare =
    dateDescendingSortCompare<RedDeer.Contracts.Dtos.Compliance.ComplianceCaseDto>(
        x => x.ReportedOn,
        x => x.Id ?? ""
    );
